import React, { useEffect, useState } from 'react';
import { IdentityLevel, TIER_META, useIdentity } from '../lib/identity';
import { useCitizenPromotion } from '../lib/sandboxPowerUp';
import { useViewport } from '../lib/useViewport';
import {
  MAKER_FEE_RATE,
  MINT_BOND_DEFAULT,
  VOUCHES_REQUIRED_BY_LENS,
  getMintBondBalance,
  getMintBondLocked,
  subscribeMintActivity,
} from '../lib/mintedMarkets';
import {
  DISCOVERY_PRICE_THRESHOLD,
  DISCOVERY_MAX_MULTIPLIER,
} from '../lib/discovery';
import { CITIZEN_DP_THRESHOLD, useUserDP } from '../lib/ledger';

// System Briefing Sidebar — v2.1.1 Ascent Ladder.
//
// Right-edge slide-out technical dossier. Content is laid out as a
// vertical Ascent Ladder (accordion) with three rungs — L1 Observer,
// L2 Citizen, L3 Sovereign. Each rung shows its own Active / Completed
// / Locked status, and is independently expand-collapsible so a
// citizen can always reach the Level-1 briefing even after levelling
// up. Below the ladder sits the evergreen Protocol Reference block
// and a Developer Cockpit (Simulate Citizenship + Reset to Observer).
// Copy is deliberately neutral and objective. All figures are surfaced
// from canonical constants so the panel cannot drift from the
// Regulatory Shield.

interface Props {
  open:    boolean;
  onClose: () => void;
}

// Sovereign Industrial palette — kept local so the sidebar has a
// discrete visual identity even when adjacent to the main cockpit.
const BG          = '#070b14';
const BG_PANEL    = '#0b1220';
const BG_BLOCK    = '#0f172a';
const RULE        = '#1e293b';
const INK         = '#e2e8f0';
const INK_MUTED   = '#94a3b8';
const INK_FAINT   = '#475569';
const GOLD        = '#fcd34d';
const EMERALD     = '#86efac';
const VIOLET      = '#a78bfa';
const AMBER       = '#f59e0b';
const CYAN        = '#7dd3fc';
const DANGER      = '#f87171';

type AscentStatus = 'ACTIVE' | 'COMPLETED' | 'LOCKED';

// Maps a rung number (1..3) against the citizen's tier to the display
// status used on the AscentSection header. Seeker (tier 0) is treated
// as a pre-Observer state — the L1 rung still reads Active, since
// that's the next aspirational step.
function statusFor(level: 1 | 2 | 3, tier: IdentityLevel): AscentStatus {
  const effective = tier === IdentityLevel.SEEKER ? IdentityLevel.OBSERVER : tier;
  if (effective > level) return 'COMPLETED';
  if (effective === level) return 'ACTIVE';
  return 'LOCKED';
}

// Which rung should auto-expand for a given tier. Citizens land on L2,
// Sovereigns on L3, everyone else on L1. Users are still free to
// expand any other rung manually.
function initialExpandedFor(tier: IdentityLevel): 1 | 2 | 3 {
  if (tier >= IdentityLevel.SOVEREIGN) return 3;
  if (tier >= IdentityLevel.CITIZEN)   return 2;
  return 1;
}

export default function SystemBriefingSidebar({ open, onClose }: Props): React.JSX.Element {
  const identity       = useIdentity();
  const tierMeta       = TIER_META[identity.tier];
  const { isMobile }   = useViewport();

  // Live Mint bond pool readout — re-reads on every mint/vouch/veto/
  // lifecycle tick so the numbers feel immediate. Falls back to 0 when
  // the identity has no id yet (pre-connect).
  const [tick, setTick] = useState(0);
  useEffect(() => subscribeMintActivity(() => setTick(t => t + 1)), []);
  const bondBalance = identity.id ? getMintBondBalance(identity.id) : 0;
  const bondLocked  = identity.id ? getMintBondLocked(identity.id)  : 0;
  void tick;

  // Observer ascent — DP aggregate streams via Supabase realtime (or
  // the local ledger). Feeds both the L1 progress bar and the L1→L2
  // threshold gate copy.
  const userDp = useUserDP(identity.id ?? '');

  // Sandbox simulate — promotes Observer → Citizen via setForcedTier,
  // standing in for the Coinbase EAS verification path. Demoted to
  // the Developer Cockpit at the bottom of the sidebar.
  const { activating, progress, promoteToCitizen } = useCitizenPromotion();

  // Accordion state — which rung is currently expanded. Re-anchors to
  // the citizen's tier whenever the tier changes (e.g., after Simulate
  // Citizenship lands), so the UI visibly pops to the new active rung.
  const [expanded, setExpanded] = useState<1 | 2 | 3 | 0>(() => initialExpandedFor(identity.tier));
  useEffect(() => {
    setExpanded(initialExpandedFor(identity.tier));
  }, [identity.tier]);

  // Transient toast for Simulate Citizenship + Reset. Auto-dismisses
  // after 2.5s. A `null` message hides the toast entirely.
  const [toast, setToast] = useState<{ text: string; accent: string } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(id);
  }, [toast]);

  async function handleSimulate(): Promise<void> {
    // v2.3.1 — passes the ghost id through so the Power-Up can also
    // inject a simulated DP floor (150 DP) alongside the tier flip.
    // Without the id, useUserDP would still read 0 and the Dynamic
    // Bond would default to the undiscounted 500 OC.
    const ok = await promoteToCitizen(identity.id ?? undefined);
    if (ok) setToast({ text: 'Simulating DP Gain… Tier Elevated to Citizen.', accent: EMERALD });
  }

  function handleReset(): void {
    // Clear every local key this sidebar touches, then reload so the
    // rest of the cockpit (balance pill, audit log, Mint Queue) refreshes
    // from the same zeroed state. A soft in-place reset is possible but
    // fragile — too many components hold derived state. The reload is
    // explicit and reliable, which is what a dev-cockpit reset wants.
    setToast({ text: 'Clearing identity & ledger…', accent: DANGER });
    try {
      window.localStorage.removeItem('agoracle_ledger_v1');
      window.localStorage.removeItem('agoracle_identity_tier');
      window.localStorage.removeItem('agoracle_briefing_seen');
      window.localStorage.removeItem('agoracle_citizen_seal');
      // v2.3.1 — drop every per-user simulated DP override. Keys are
      // prefixed `agoracle_simulated_dp:<userId>`; sweep the whole
      // namespace so the reset is exhaustive even if the sandbox has
      // cycled through multiple ghost ids in one session.
      const stale: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k && k.indexOf('agoracle_simulated_dp') === 0) stale.push(k);
      }
      stale.forEach(k => window.localStorage.removeItem(k));
    } catch { /* storage denied */ }
    // Short delay so the toast flashes before the reload wipes it.
    setTimeout(() => { window.location.reload(); }, 350);
  }

  return (
    <>
      {/* Backdrop — softened so the cockpit remains legible behind the
          dossier. Clicking dismisses the panel. Inert when closed. */}
      <div
        onClick={onClose}
        style={{
          position:         'fixed',
          inset:            0,
          zIndex:           1200,
          backgroundColor:  open ? 'rgba(2,4,14,0.42)' : 'transparent',
          backdropFilter:   open ? 'blur(2px)' : 'none',
          WebkitBackdropFilter: open ? 'blur(2px)' : 'none',
          pointerEvents:    open ? 'auto' : 'none',
          transition:       'background-color 0.25s ease, backdrop-filter 0.25s ease',
        }}
      />

      <aside
        aria-label={isMobile ? 'Command Drawer' : 'System Briefing'}
        aria-hidden={!open}
        style={{
          position:        'fixed',
          top:             28,                     // sits below the Hazard Stripe
          right:           0,
          bottom:          0,
          // v2.4 Command Drawer — full-bleed on mobile (< 768 px) so
          // the sidebar becomes the primary tactical surface; sized
          // panel on desktop.
          width:           isMobile ? '100vw' : 'min(92vw, 380px)',
          zIndex:          1250,
          background:      BG,
          borderLeft:      isMobile ? 'none' : `2px solid ${tierMeta.border}`,
          boxShadow:       open ? '-24px 0 48px rgba(0,0,0,0.55)' : 'none',
          transform:       open ? 'translateX(0)' : 'translateX(100%)',
          transition:      'transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 0.28s ease',
          display:         'flex',
          flexDirection:   'column',
          fontFamily:      'monospace',
          color:           INK,
        }}
      >
        {/* ─── Toast ──────────────────────────────────────────────── */}
        <Toast toast={toast} />

        {/* ─── Header ──────────────────────────────────────────────── */}
        <div style={{
          padding:        '12px 14px',
          borderBottom:   `1px solid ${RULE}`,
          display:        'flex',
          alignItems:     'center',
          gap:            10,
          background:     BG_PANEL,
        }}>
          <span aria-hidden="true" style={{
            fontSize:      16,
            color:         tierMeta.color,
            lineHeight:    1,
            textShadow:    tierMeta.shadow ?? 'none',
          }}>⌬</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize:      9,
              letterSpacing: '0.28em',
              color:         GOLD,
              fontWeight:    800,
            }}>
              {isMobile ? 'COMMAND DRAWER' : 'SYSTEM BRIEFING'}
            </div>
            <div style={{
              fontSize:      8,
              letterSpacing: '0.18em',
              color:         tierMeta.color,
              marginTop:     2,
              textTransform: 'uppercase',
            }}>
              TIER {identity.tier} · {tierMeta.label}
            </div>
          </div>
          {/* Desktop-only inline × — on mobile the dismiss affordance is
              rendered as a viewport-fixed pill below (outside the aside)
              so it can't be swallowed by any stacking context or hidden
              behind the iPhone notch / Safari chrome. */}
          {!isMobile && (
            <button
              onClick={onClose}
              aria-label="Close System Briefing"
              title="Close (Esc)"
              style={{
                background:    'none',
                border:        `1px solid ${RULE}`,
                color:         INK_MUTED,
                width:         26,
                height:        26,
                borderRadius:  4,
                cursor:        'pointer',
                fontFamily:    'monospace',
                fontSize:      14,
                lineHeight:    1,
              }}
            >×</button>
          )}
        </div>

        {/* ─── Scrolling body ────────────────────────────────────────
              Flex-1 with minHeight:0 is the canonical pattern for a
              scrollable child inside a flex column — without
              minHeight:0 the inner overflow is ignored and the body
              grows to fit its children, pushing tail content below
              the viewport.
              Bottom padding is deliberately heavy (96 px): the feedback
              button sits at bottom:20 / right:20 with a 40 px tall pill,
              so ~80 px of runway is needed before the final Protocol
              Reference block and the Architect signature stop feeling
              trapped under the viewport edge. overscroll-behavior:
              contain stops the outer page from scroll-chaining when the
              dossier hits its own extent. */}
        <div style={{
          flex:             1,
          minHeight:        0,
          overflowY:        'auto',
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          padding:          '12px 14px 96px',
          display:          'flex',
          flexDirection:    'column',
          gap:              10,
        }}>
          {/* ── Mobile-only in-drawer CLOSE bar ─────────────────────
                Full-width sovereign-gold pill as the first child of
                the scrolling body. Lives inside normal document flow
                (no position:fixed, no stacking context tricks) so if
                the user can see the drawer, they can see this bar.
                Redundant with the viewport-fixed DISMISS pill below
                — two independent dismiss paths survive any one
                rendering or stacking failure. */}
          {isMobile && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close Command Drawer"
              style={{
                width:           '100%',
                display:         'flex',
                alignItems:      'center',
                justifyContent:  'center',
                gap:             10,
                background:      '#fcd34d',
                border:          '2px solid #78350f',
                color:           '#1a1200',
                minHeight:       56,
                padding:         '14px 18px',
                borderRadius:    10,
                cursor:          'pointer',
                fontFamily:      'monospace',
                fontSize:        13,
                fontWeight:      900,
                letterSpacing:   '0.24em',
                lineHeight:      1,
                boxShadow:       '0 0 22px rgba(252,211,77,0.55), 0 4px 14px rgba(0,0,0,0.45)',
                marginBottom:    4,
                WebkitTapHighlightColor: 'transparent',
                WebkitAppearance: 'none',
                appearance:      'none',
              }}
            >
              <span aria-hidden="true" style={{ fontSize: 18, fontWeight: 900 }}>✕</span>
              CLOSE DRAWER
            </button>
          )}

          {/* The Ascent Ladder — three rungs, always present, each
              independently expand/collapsible. Content is tier-aware
              but never hidden: a Citizen can still expand the L1 rung
              to re-read the Observer briefing. */}
          <div style={{
            fontSize:      8,
            letterSpacing: '0.28em',
            color:         INK_FAINT,
            fontWeight:    700,
            marginBottom:  2,
          }}>
            THE ASCENT LADDER
          </div>

          <AscentSection
            level={1}
            tier="OBSERVER"
            accent={EMERALD}
            status={statusFor(1, identity.tier)}
            expanded={expanded === 1}
            onToggle={() => setExpanded(e => (e === 1 ? 0 : 1))}
          >
            <Level1Body userDp={userDp} tier={identity.tier} />
          </AscentSection>

          <AscentSection
            level={2}
            tier="CITIZEN"
            accent={GOLD}
            status={statusFor(2, identity.tier)}
            expanded={expanded === 2}
            onToggle={() => setExpanded(e => (e === 2 ? 0 : 2))}
          >
            <Level2Body
              unlocked={identity.tier >= IdentityLevel.CITIZEN}
              bondBalance={bondBalance}
              bondLocked={bondLocked}
              tier={identity.tier}
            />
          </AscentSection>

          <AscentSection
            level={3}
            tier="SOVEREIGN"
            accent={VIOLET}
            status={statusFor(3, identity.tier)}
            expanded={expanded === 3}
            onToggle={() => setExpanded(e => (e === 3 ? 0 : 3))}
          >
            <Level3Body unlocked={identity.tier >= IdentityLevel.SOVEREIGN} />
          </AscentSection>

          {/* Evergreen protocol reference — applies to every tier. */}
          <div style={{
            fontSize:      8,
            letterSpacing: '0.28em',
            color:         INK_FAINT,
            fontWeight:    700,
            marginTop:     8,
            marginBottom:  2,
          }}>
            PROTOCOL REFERENCE
          </div>

          <InstructionBlock
            icon="📐"
            label="THE DIVERGENCE ENGINE"
            accent={EMERALD}
            body="The Agoracle measures the gap between Price and Reality. Accuracy earns Divergence Points."
            formula="DP = Σ units × (1 − price)"
            footnote="High-probability favourites provide steady growth. Low-probability outsiders provide high-velocity reputation."
          />

          <InstructionBlock
            icon="🛡"
            label="THE SAFETY VALVES"
            accent={VIOLET}
            body="Pledges are protected by a deterministic arbitration layer. If a Resolution Source returns no signal, the market enters STALE. A Sovereign quorum may Veto between T+24h and T+72h — fraudulent markets burn the creator's bond. If no Veto fires, Auto-Refund triggers at T+72h and all pledgers are made whole 1:1."
          />

          <InstructionBlock
            icon="⚖"
            label="PRICE AS PROBABILITY"
            accent={INK_MUTED}
            body="A price of 0.80 means the market's implied probability of the outcome is 80%. Prices always sum to exactly 1.0 across a Minted market's outcome slate — this is the integrity of the Mint."
          />

          {/* Developer Cockpit — sandbox-only affordances. */}
          <DeveloperCockpit
            activating={activating}
            progress={progress}
            onSimulate={handleSimulate}
            onReset={handleReset}
            isMobile={isMobile}
          />

          {/* Architect attribution — same sovereign signature used in
              the footer and CHRONICLE headers. */}
          <div style={{
            marginTop:     4,
            paddingTop:    10,
            borderTop:     `1px dashed ${RULE}`,
            fontSize:      8,
            letterSpacing: '0.22em',
            color:         INK_FAINT,
            textAlign:     'center',
          }}>
            ARCHITECT · DROMIDE
          </div>
        </div>
      </aside>

      {/* ── Mobile viewport-fixed DISMISS pill ─────────────────────────
            Rendered as a sibling of the <aside> so it is NOT nested
            inside the drawer's stacking context. Anchored to the
            BOTTOM-RIGHT (not top) for two reasons:
              1. The index.html Hazard Stripe sits at top:0 with z-index
                 2147483647 — the max 32-bit int — so anything positioned
                 in the top band loses the stacking fight regardless of
                 where it lives in the DOM. Bottom-anchored is guaranteed
                 clear of that.
              2. iOS Safari's top toolbar and notch / dynamic island are
                 unpredictable targets; the bottom home-indicator area
                 is stable and env(safe-area-inset-bottom) is widely
                 supported.
            z-index matches the Hazard Stripe's max value and — since
            this pill comes later in document order — wins ties. Solid
            gold fill + dark text + amber glow for maximum contrast on
            the cockpit's blue-grey background. Always mounted;
            visibility/opacity/transform animate so slow iOS devices
            never race a mount transition. */}
      {isMobile && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss Command Drawer"
          aria-hidden={!open}
          style={{
            position:        'fixed',
            bottom:          'calc(env(safe-area-inset-bottom, 0px) + 20px)',
            right:           'calc(env(safe-area-inset-right, 0px) + 20px)',
            zIndex:          2147483647,
            display:         'inline-flex',
            alignItems:      'center',
            gap:             8,
            background:      '#fcd34d',
            border:          '2px solid #78350f',
            color:           '#1a1200',
            minHeight:       52,
            padding:         '12px 22px',
            borderRadius:    26,
            cursor:          'pointer',
            fontFamily:      'monospace',
            fontSize:        13,
            fontWeight:      900,
            letterSpacing:   '0.22em',
            lineHeight:      1,
            boxShadow:       '0 0 24px rgba(252,211,77,0.85), 0 8px 20px rgba(0,0,0,0.6)',
            visibility:      open ? 'visible' : 'hidden',
            opacity:         open ? 1 : 0,
            pointerEvents:   open ? 'auto' : 'none',
            transform:       open ? 'translateY(0)' : 'translateY(12px)',
            transition:      'opacity 0.2s ease, transform 0.2s ease',
            WebkitTapHighlightColor: 'transparent',
            WebkitAppearance: 'none',
            appearance:      'none',
          }}
        >
          <span aria-hidden="true" style={{ fontSize: 18, fontWeight: 900 }}>✕</span>
          DISMISS
        </button>
      )}
    </>
  );
}

// ─── Toast ─────────────────────────────────────────────────────────────

function Toast({ toast }: { toast: { text: string; accent: string } | null }): React.JSX.Element {
  // Rendered even when `toast` is null so the transition can animate
  // out cleanly; display: toggled via opacity + transform.
  const visible = toast !== null;
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position:       'absolute',
        top:            10,
        left:           '50%',
        transform:      visible ? 'translate(-50%, 0)' : 'translate(-50%, -8px)',
        opacity:        visible ? 1 : 0,
        pointerEvents:  visible ? 'auto' : 'none',
        transition:     'opacity 0.2s ease, transform 0.2s ease',
        zIndex:         1300,
        padding:        '6px 12px',
        background:     BG_PANEL,
        border:         `1px solid ${toast?.accent ?? RULE}`,
        borderRadius:   4,
        fontFamily:     'monospace',
        fontSize:       10,
        letterSpacing:  '0.08em',
        color:          toast?.accent ?? INK_MUTED,
        boxShadow:      `0 0 14px ${toast?.accent ?? RULE}33`,
        whiteSpace:     'nowrap',
        maxWidth:       'calc(100% - 40px)',
        overflow:       'hidden',
        textOverflow:   'ellipsis',
      }}
    >
      {toast?.text ?? ''}
    </div>
  );
}

// ─── Ascent Ladder primitives ──────────────────────────────────────────

function AscentSection({
  level, tier, accent, status, expanded, onToggle, children,
}: {
  level:    1 | 2 | 3;
  tier:     string;
  accent:   string;
  status:   AscentStatus;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  const locked   = status === 'LOCKED';
  const done     = status === 'COMPLETED';
  const active   = status === 'ACTIVE';
  const statusColor = active ? accent : done ? EMERALD : INK_FAINT;
  const borderColor = active ? accent : done ? '#14532d' : RULE;
  // v2.1.4 — The smooth grid-rows animation was chopping L1 content
  // in the wild (observed: "Anchoring / Scouting" paragraphs clipped,
  // section border wrapping the truncated text). Per the Architect's
  // aggressive-fix brief, the animation is dropped entirely — a pop-
  // open that renders every paragraph is worth more than a smooth
  // transition that eats instructions. What this rewrite enforces:
  //   * `flex-shrink: 0` on the section so the flex-column scrollable
  //     body cannot squish it below its natural height.
  //   * `overflow: visible` on the section — the original `hidden`
  //     combined with flex collapse was the actual clipper.
  //   * `height: auto` on the expanded body, no max-height, no grid
  //     track interpolation — just straight conditional render.
  return (
    <section style={{
      background:    BG_BLOCK,
      border:        `1px solid ${borderColor}`,
      borderRadius:  6,
      overflow:      'visible',
      flexShrink:    0,
      opacity:       locked && !expanded ? 0.62 : 1,
      transition:    'opacity 0.2s ease, border-color 0.2s ease',
    }}>
      <button
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          width:          '100%',
          padding:        '10px 12px',
          background:     expanded ? BG_PANEL : 'transparent',
          border:         'none',
          borderBottom:   expanded ? `1px solid ${RULE}` : 'none',
          borderRadius:   expanded ? '6px 6px 0 0' : 6,
          cursor:         'pointer',
          display:        'flex',
          alignItems:     'center',
          gap:            10,
          fontFamily:     'monospace',
          color:          INK,
          textAlign:      'left',
          transition:     'background 0.15s ease',
        }}
      >
        <span style={{
          fontSize:      9,
          letterSpacing: '0.22em',
          color:         statusColor,
          fontWeight:    800,
          width:         38,
        }}>
          L{level}
        </span>
        <span style={{
          fontSize:      11,
          letterSpacing: '0.18em',
          color:         active ? accent : INK,
          fontWeight:    800,
          flex:          1,
          minWidth:      0,
        }}>
          {tier}
        </span>
        <span style={{
          fontSize:      7,
          letterSpacing: '0.22em',
          color:         statusColor,
          border:        `1px solid ${statusColor}`,
          padding:       '2px 6px',
          borderRadius:  3,
          fontWeight:    800,
          background:    active ? `${accent}14` : 'transparent',
        }}>
          {status}
        </span>
        <span aria-hidden="true" style={{
          fontSize: 11,
          color:    INK_MUTED,
          width:    12,
          textAlign:'center',
        }}>
          {expanded ? '▾' : '▸'}
        </span>
      </button>
      {expanded && (
        <div style={{
          // 32 px bottom padding (pb-8) gives the last Spectrum row
          // inside L1 a comfortable tail — the previous 12 px was
          // tight enough that the section border visibly hugged the
          // final paragraph's descenders.
          padding:        '12px 14px 32px',
          display:        'flex',
          flexDirection:  'column',
          gap:            10,
          background:     BG_BLOCK,
          height:         'auto',
          overflow:       'visible',
          flexShrink:     0,
        }}>
          {children}
        </div>
      )}
    </section>
  );
}

// ─── Level bodies ──────────────────────────────────────────────────────

function Level1Body({ userDp, tier }: { userDp: number; tier: IdentityLevel }): React.JSX.Element {
  const pct = Math.min(100, Math.max(0, (userDp / CITIZEN_DP_THRESHOLD) * 100));
  const atThreshold = userDp >= CITIZEN_DP_THRESHOLD;
  const alreadyCitizen = tier >= IdentityLevel.CITIZEN;

  return (
    <>
      {/* Prominent status line — directly answers "where am I and what
          do I need?" so the citizen never has to infer the state. */}
      <p style={{
        margin:        0,
        padding:       '8px 10px',
        background:    BG_PANEL,
        border:        `1px solid ${RULE}`,
        borderLeft:    `2px solid ${EMERALD}`,
        borderRadius:  3,
        fontSize:      10,
        lineHeight:    1.55,
        color:         INK,
      }}>
        {alreadyCitizen ? (
          <>You have cleared the Observer rung. Level 1 is <b style={{ color: EMERALD }}>complete</b> — this briefing is kept here for reference.</>
        ) : (
          <>You are currently an <b style={{ color: EMERALD }}>Observer</b>. Your <b style={{ color: GOLD }}>1,000 OC</b> is for Pledging only. Unlocking Level 2 requires <b style={{ color: EMERALD }}>{CITIZEN_DP_THRESHOLD} DP</b>.</>
        )}
      </p>

      {/* 1,000 OC Genesis Grant tile — single value, pledging-only. */}
      <div style={{
        background:   BG_PANEL,
        border:       `1px solid ${RULE}`,
        borderLeft:   `2px solid ${GOLD}`,
        borderRadius: 4,
        padding:      '10px 12px',
      }}>
        <div style={{ fontSize: 7, letterSpacing: '0.22em', color: INK_FAINT, fontWeight: 700 }}>
          GENESIS GRANT · ON CONNECT
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 3 }}>
          <span style={{ fontSize: 20, color: GOLD, fontWeight: 800, letterSpacing: '0.04em' }}>
            1,000 OC
          </span>
          <span style={{ fontSize: 9, color: INK_MUTED, letterSpacing: '0.14em' }}>
            · PLEDGE
          </span>
        </div>
        <div style={{ fontSize: 9, color: INK_MUTED, marginTop: 3, lineHeight: 1.5 }}>
          Use it to build your signal history. All 1,000 OC is available for pledging.
        </div>
      </div>

      {/* DP Progress Bar — the single, clear ascent indicator. */}
      <div>
        <div style={{
          display:       'flex',
          alignItems:    'baseline',
          justifyContent:'space-between',
          fontSize:      8,
          letterSpacing: '0.22em',
          color:         INK_FAINT,
          fontWeight:    700,
          marginBottom:  4,
        }}>
          <span>DIVERGENCE POINTS · ASCENT</span>
          <span style={{ color: atThreshold ? GOLD : EMERALD }}>
            {userDp.toFixed(1)} / {CITIZEN_DP_THRESHOLD} DP
          </span>
        </div>
        <div style={{
          height:       6,
          background:   BG_PANEL,
          border:       `1px solid ${RULE}`,
          borderRadius: 3,
          overflow:     'hidden',
        }}>
          <div style={{
            height:          '100%',
            width:           `${pct}%`,
            backgroundColor: atThreshold ? GOLD : EMERALD,
            boxShadow:       atThreshold
              ? '0 0 10px rgba(252,211,77,0.55)'
              : '0 0 8px rgba(134,239,172,0.35)',
            transition:      'width 0.5s ease, background-color 0.3s ease',
          }} />
        </div>
      </div>

      {/* The Message — verbatim from the Unified Meritocracy brief. */}
      <p style={{
        margin:        0,
        fontSize:      10,
        lineHeight:    1.55,
        color:         INK_MUTED,
        letterSpacing: '0.02em',
        paddingTop:    8,
        borderTop:     `1px dashed ${RULE}`,
      }}>
        Your reputation (DP) grows with every accurate signal. The Agoracle is a spectrum: you can build steady reputation by confirming certainties, or accelerate it by discovering outliers. Every accurate pledge is a civic contribution.
      </p>

      {/* The Spectrum of Contribution — two behaviours, not two classes. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>〰</span>
          <span style={{
            fontSize:      8,
            letterSpacing: '0.26em',
            color:         CYAN,
            fontWeight:    800,
          }}>
            THE SPECTRUM OF CONTRIBUTION
          </span>
        </div>
        <SpectrumRow
          label="ANCHORING"
          accent={EMERALD}
          body="Pledging on high-probability outcomes (Favorites). Low risk, steady DP accumulation. This provides the stable baseline for reality."
        />
        <SpectrumRow
          label="SCOUTING"
          accent={AMBER}
          body={`Pledging on low-probability outcomes (Outsiders < ${DISCOVERY_PRICE_THRESHOLD.toFixed(2)}). High risk, high reward. Correcting the crowd on a 'black swan' triggers a Discovery Bonus (up to ${DISCOVERY_MAX_MULTIPLIER.toFixed(1)}×).`}
        />
      </div>
    </>
  );
}

function Level2Body({
  unlocked, bondBalance, bondLocked, tier,
}: {
  unlocked:    boolean;
  bondBalance: number;
  bondLocked:  number;
  tier:        IdentityLevel;
}): React.JSX.Element {
  const isSovereign = tier >= IdentityLevel.SOVEREIGN;
  const accent = isSovereign ? GOLD : unlocked ? GOLD : INK_MUTED;

  return (
    <>
      <p style={{
        margin:        0,
        padding:       '8px 10px',
        background:    BG_PANEL,
        border:        `1px solid ${RULE}`,
        borderLeft:    `2px solid ${accent}`,
        borderRadius:  3,
        fontSize:      10,
        lineHeight:    1.55,
        color:         INK,
      }}>
        {unlocked ? (
          <>
            <b style={{ color: GOLD, letterSpacing: '0.14em', fontSize: 8 }}>CITIZENSHIP UNLOCKED · </b>
            You may now Mint Markets. A <b style={{ color: GOLD }}>Dynamic Bond</b> locks for each Minted Truth — {MINT_BOND_DEFAULT} OC at zero DP, discounted by your Trust Matrix — and you earn a <b style={{ color: GOLD }}>{(MAKER_FEE_RATE * 100).toFixed(1)}% Maker Fee</b> on all volume your markets settle.
          </>
        ) : (
          <>
            Reach <b style={{ color: EMERALD }}>{CITIZEN_DP_THRESHOLD} DP</b> to unlock the ability to Mint new markets. Each Minted Truth locks a <b style={{ color: GOLD }}>Dynamic Bond</b> (starting at {MINT_BOND_DEFAULT} OC, discounted by DP), returned 1:1 on clean resolution; Citizens also earn a <b style={{ color: GOLD }}>{(MAKER_FEE_RATE * 100).toFixed(1)}%</b> Maker Fee on all volume their markets settle.
          </>
        )}
      </p>

      {/* Only surface the live bond pool when the citizen has actually
          unlocked L2 — otherwise the numbers are noise. */}
      {unlocked && (
        <div style={{
          display:             'grid',
          gridTemplateColumns: '1fr 1fr',
          gap:                 8,
        }}>
          <StatTile label="MINT BOND POOL" value={`${bondBalance.toLocaleString()} OC`} accent={accent} />
          <StatTile label="BONDS LOCKED"   value={`${bondLocked.toLocaleString()} OC`} accent={INK_MUTED} />
        </div>
      )}

      <div style={{
        background:   BG_PANEL,
        border:       `1px solid ${RULE}`,
        borderRadius: 4,
        padding:      '8px 10px',
      }}>
        <div style={{ fontSize: 8, letterSpacing: '0.22em', color: INK_FAINT, fontWeight: 700 }}>
          VOUCH THRESHOLDS · BY LENS
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
          {(['LOCAL', 'ASIA', 'EUROPE', 'GLOBAL'] as const).map(lens => (
            <div key={lens} style={{
              display:       'flex',
              alignItems:    'center',
              gap:           8,
              fontSize:      9,
              letterSpacing: '0.12em',
            }}>
              <span style={{ color: INK, width: 60 }}>{lens}</span>
              <span style={{ flex: 1, height: 1, background: RULE }} />
              <span style={{ color: accent, fontWeight: 800 }}>
                {VOUCHES_REQUIRED_BY_LENS[lens]} VOUCH{VOUCHES_REQUIRED_BY_LENS[lens] === 1 ? '' : 'ES'}
              </span>
            </div>
          ))}
          <div style={{
            marginTop:     4,
            fontSize:      8,
            letterSpacing: '0.14em',
            color:         VIOLET,
          }}>
            GLOBAL · 1 SOVEREIGN VOUCH BYPASSES THE QUEUE
          </div>
        </div>
      </div>

      <InstructionBlock
        icon="💎"
        label="THE MINT"
        accent={GOLD}
        body={`Any Citizen may Mint a market. A Dynamic Creation Bond locks at mint — ${MINT_BOND_DEFAULT} OC at zero DP, shaved by 0.5 OC per DP down to a 100 OC floor — and is returned 1:1 on clean resolution. The Creator earns a ${(MAKER_FEE_RATE * 100).toFixed(1)}% Maker Fee on every OC of volume that settles against their market — the incentive to Mint truths that Citizens want to pledge against. Frivolous or adversarial markets forfeit the bond.`}
      />

      <InstructionBlock
        icon="📡"
        label="SIGNAL DISCOVERY"
        accent={AMBER}
        body={`Outcomes priced below ${(DISCOVERY_PRICE_THRESHOLD * 100).toFixed(0)}% carry a Discovery Bonus of up to ${DISCOVERY_MAX_MULTIPLIER.toFixed(1)}×, scaled by how early the pledge entered the market's lifetime. This bounty compensates the deep research required to find hidden signals before the crowd.`}
      />
    </>
  );
}

function Level3Body({ unlocked }: { unlocked: boolean }): React.JSX.Element {
  return (
    <>
      <p style={{
        margin:        0,
        padding:       '8px 10px',
        background:    BG_PANEL,
        border:        `1px solid ${RULE}`,
        borderLeft:    `2px solid ${VIOLET}`,
        borderRadius:  3,
        fontSize:      10,
        lineHeight:    1.55,
        color:         INK,
      }}>
        {unlocked ? (
          <>
            <b style={{ color: VIOLET, letterSpacing: '0.14em', fontSize: 8 }}>SOVEREIGN STATUS · </b>
            You hold the Veto and sit on the Justice quorum. Your weight multiplier is <b style={{ color: VIOLET }}>2.0×</b>.
          </>
        ) : (
          <>The <b style={{ color: VIOLET }}>Sovereign</b> tier is earned through aged identity and sustained reputation. It unlocks the Veto during the challenge window and participation in Justice oversight of adversarial markets.</>
        )}
      </p>

      <InstructionBlock
        icon="⚔"
        label="THE SOVEREIGN VETO"
        accent={VIOLET}
        body="Between T+24h and T+72h after settlement is initiated, a Sovereign quorum may Veto a fraudulent market. A successful Veto burns the creator's Creation Bond and routes refunds 1:1 to every pledger."
      />

      <InstructionBlock
        icon="⚖"
        label="JUSTICE OVERSIGHT"
        accent={VIOLET}
        body="Sovereigns review disputed resolutions, bond forfeitures, and edge-case settlements. The quorum is tier-weighted — a single Sovereign Vouch bypasses Lens queues for trusted markets."
      />
    </>
  );
}

// ─── Developer Cockpit ─────────────────────────────────────────────────

function DeveloperCockpit({
  activating, progress, onSimulate, onReset, isMobile,
}: {
  activating: boolean;
  progress:   number;
  onSimulate: () => Promise<void>;
  onReset:    () => void;
  isMobile:   boolean;
}): React.JSX.Element {
  return (
    <section style={{
      marginTop:      8,
      padding:        isMobile ? '14px 14px' : '10px 12px',
      background:     BG_PANEL,
      border:         `1px dashed ${RULE}`,
      borderRadius:   4,
      display:        'flex',
      flexDirection:  'column',
      gap:            isMobile ? 12 : 8,
    }}>
      <div style={{
        fontSize:      7,
        letterSpacing: '0.28em',
        color:         INK_FAINT,
        fontWeight:    700,
      }}>
        DEVELOPER COCKPIT · SANDBOX ONLY
      </div>

      <button
        onClick={() => { void onSimulate(); }}
        disabled={activating}
        style={{
          padding:         isMobile ? '14px 16px' : '8px 12px',
          minHeight:       isMobile ? 48 : undefined,
          borderRadius:    4,
          border:          `1px solid ${activating ? INK_FAINT : EMERALD}`,
          background:      activating ? '#0a0f1c' : 'transparent',
          color:           activating ? INK_MUTED : EMERALD,
          fontFamily:      'monospace',
          fontSize:        isMobile ? 12 : 9,
          fontWeight:      800,
          letterSpacing:   '0.22em',
          cursor:          activating ? 'progress' : 'pointer',
          transition:      'border-color 0.15s, color 0.15s, background 0.15s',
        }}
      >
        {activating
          ? `⎔ SIMULATING… ${Math.round(progress)}%`
          : '[ SIMULATE CITIZENSHIP ]'}
      </button>

      {activating && (
        <div style={{ height: 2, background: RULE, borderRadius: 1, overflow: 'hidden' }}>
          <div style={{
            height:          '100%',
            width:           `${progress}%`,
            backgroundColor: EMERALD,
            transition:      'width 0.1s linear',
          }} />
        </div>
      )}

      <button
        onClick={onReset}
        title="Clear local ledger, identity override, and briefing-seen flag, then reload."
        style={{
          padding:         isMobile ? '12px 14px' : '6px 10px',
          minHeight:       isMobile ? 44 : undefined,
          borderRadius:    4,
          border:          `1px solid ${DANGER}55`,
          background:      'transparent',
          color:           DANGER,
          fontFamily:      'monospace',
          fontSize:        isMobile ? 11 : 8,
          fontWeight:      700,
          letterSpacing:   '0.22em',
          cursor:          'pointer',
          transition:      'border-color 0.15s, color 0.15s, background 0.15s',
        }}
        onMouseEnter={e => {
          e.currentTarget.style.borderColor = DANGER;
          e.currentTarget.style.background  = `${DANGER}12`;
        }}
        onMouseLeave={e => {
          e.currentTarget.style.borderColor = `${DANGER}55`;
          e.currentTarget.style.background  = 'transparent';
        }}
      >
        [ RESET TO OBSERVER ]
      </button>

      <div style={{
        fontSize:      8,
        letterSpacing: '0.14em',
        color:         INK_FAINT,
        textAlign:     'center',
      }}>
        Real ascent is earned via DP.
      </div>
    </section>
  );
}

// ─── Presentational primitives ─────────────────────────────────────────

function SpectrumRow({ label, accent, body }: {
  label: string; accent: string; body: string;
}): React.JSX.Element {
  return (
    <div style={{
      display:      'flex',
      flexDirection:'column',
      gap:          2,
      paddingLeft:  8,
      borderLeft:   `1px solid ${accent}`,
    }}>
      <span style={{
        fontSize:      8,
        letterSpacing: '0.22em',
        color:         accent,
        fontWeight:    800,
      }}>
        {label}
      </span>
      <p style={{
        margin:     0,
        fontSize:   10,
        lineHeight: 1.5,
        color:      INK,
      }}>
        {body}
      </p>
    </div>
  );
}

function StatTile({ label, value, accent }: {
  label: string; value: string; accent: string;
}): React.JSX.Element {
  return (
    <div style={{
      background:   BG_PANEL,
      border:       `1px solid ${RULE}`,
      borderRadius: 4,
      padding:      '8px 10px',
    }}>
      <div style={{ fontSize: 7, letterSpacing: '0.22em', color: INK_FAINT, fontWeight: 700 }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: accent, fontWeight: 800, marginTop: 2 }}>
        {value}
      </div>
    </div>
  );
}

function InstructionBlock({
  icon, label, accent, body, formula, footnote,
}: {
  icon:      string;
  label:     string;
  accent:    string;
  body:      string;
  formula?:  string;
  footnote?: string;
}): React.JSX.Element {
  return (
    <section style={{
      background:   BG_PANEL,
      border:       `1px solid ${RULE}`,
      borderLeft:   `2px solid ${accent}`,
      borderRadius: 4,
      padding:      '10px 12px',
      display:      'flex',
      flexDirection:'column',
      gap:          6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>{icon}</span>
        <span style={{
          fontSize:      8,
          letterSpacing: '0.26em',
          color:         accent,
          fontWeight:    800,
        }}>
          {label}
        </span>
      </div>
      <p style={{
        margin:        0,
        fontSize:      10,
        lineHeight:    1.55,
        color:         INK,
        letterSpacing: '0.02em',
      }}>
        {body}
      </p>
      {formula && (
        <div style={{
          background:   BG_BLOCK,
          border:       `1px solid ${RULE}`,
          borderRadius: 3,
          padding:      '6px 8px',
          fontSize:     11,
          color:        accent,
          letterSpacing:'0.08em',
          textAlign:    'center',
        }}>
          {formula}
        </div>
      )}
      {footnote && (
        <p style={{
          margin:        0,
          fontSize:      9,
          lineHeight:    1.5,
          color:         INK_MUTED,
          letterSpacing: '0.02em',
        }}>
          {footnote}
        </p>
      )}
    </section>
  );
}
